(() => {
  if (window.UpCartEnhancementsLoaded) return;
  window.UpCartEnhancementsLoaded = true;

  const root = window.Shopify?.routes?.root || '/';
  const memoryStorage = new Map();
  let giftSyncInProgress = false;
  let enhancementTimer = null;

  const safeStorage = {
    get(key) {
      try {
        return window.localStorage.getItem(key);
      } catch (_) {
        return memoryStorage.get(key) || null;
      }
    },
    set(key, value) {
      try {
        window.localStorage.setItem(key, value);
      } catch (_) {
        memoryStorage.set(key, value);
      }
    },
    remove(key) {
      try {
        window.localStorage.removeItem(key);
      } catch (_) {
        memoryStorage.delete(key);
      }
    },
  };

  const cartSections = () => {
    const sections = ['cart-drawer'];
    if (document.getElementById('cart-icon-bubble')) sections.push('cart-icon-bubble');
    return sections;
  };

  const parseJsonResponse = async (response) => {
    const data = await response.json();
    if (!response.ok) {
      throw new Error(data.description || data.message || 'Erreur du panier');
    }
    return data;
  };

  const renderCartResponse = (data) => {
    const drawer = document.querySelector('cart-drawer');
    if (drawer?.renderContents && data?.sections?.['cart-drawer']) {
      drawer.renderContents(data);
    }
  };

  /* ------------------------------------------------------------------ */
  /* Compte à rebours                                                   */
  /* ------------------------------------------------------------------ */

  const getCountdownState = (element) => {
    const duration = Math.max(1, Number.parseInt(element.dataset.duration || '10', 10));
    const storageKey = `up_cart_countdown_end_${duration}`;
    const storedValue = Number.parseInt(safeStorage.get(storageKey) || '0', 10);
    let end = storedValue || Number.parseInt(element.dataset.fallbackEnd || '0', 10);

    if (!end || end <= Date.now()) {
      if (end && element.dataset.restart !== 'true') {
        return { end, storageKey, expired: true };
      }

      end = Date.now() + duration * 60_000;
      element.dataset.fallbackEnd = String(end);
      safeStorage.set(storageKey, String(end));
    }

    return { end, storageKey, expired: false };
  };

  const updateCountdownElement = (element) => {
    const state = getCountdownState(element);
    const clock = element.querySelector('.up-cart-countdown__clock');
    const expiredTarget = element.querySelector('[data-countdown-expired]');

    if (state.expired) {
      if (clock) clock.hidden = true;
      if (expiredTarget) {
        expiredTarget.hidden = false;
        expiredTarget.textContent = element.dataset.expiredText || 'Le délai est écoulé';
      }
      return;
    }

    const remaining = Math.max(0, state.end - Date.now());
    const showHours = element.dataset.showHours === 'true';
    const totalMinutes = Math.floor(remaining / 60_000);
    const hours = showHours ? Math.floor(totalMinutes / 60) : 0;
    const minutes = showHours ? totalMinutes % 60 : totalMinutes;
    const seconds = Math.floor((remaining % 60_000) / 1000);

    const hoursTarget = element.querySelector('[data-countdown-hours]');
    const minutesTarget = element.querySelector('[data-countdown-minutes]');
    const secondsTarget = element.querySelector('[data-countdown-seconds]');

    if (hoursTarget) hoursTarget.textContent = String(hours).padStart(2, '0');
    if (minutesTarget) minutesTarget.textContent = String(minutes).padStart(2, '0');
    if (secondsTarget) secondsTarget.textContent = String(seconds).padStart(2, '0');
    if (clock) clock.hidden = false;
    if (expiredTarget) expiredTarget.hidden = true;

    if (remaining <= 0) {
      safeStorage.remove(state.storageKey);
    }
  };

  const updateCountdowns = () => {
    document.querySelectorAll('[data-cart-countdown]').forEach(updateCountdownElement);
  };

  window.setInterval(updateCountdowns, 1000);

  /* ------------------------------------------------------------------ */
  /* Navigation horizontale partagée                                   */
  /* ------------------------------------------------------------------ */

  const prefersReducedMotion = window.matchMedia(
    '(prefers-reduced-motion: reduce)'
  ).matches;

  const setupCartCarousel = ({
    container,
    track,
    previousButton,
    nextButton,
    counter,
    itemSelector,
    controls,
  }) => {
    if (!container || !track || track.dataset.carouselInitialized === 'true') {
      return null;
    }

    const items = [...track.querySelectorAll(itemSelector)];
    if (!items.length) return null;

    track.dataset.carouselInitialized = 'true';

    if (items.length <= 1) {
      if (controls) controls.hidden = true;
      if (previousButton) previousButton.disabled = true;
      if (nextButton) nextButton.disabled = true;
      if (counter) counter.textContent = `1 / ${items.length}`;
      return null;
    }

    if (controls) controls.hidden = false;

    let activeIndex = 0;
    let frame = null;

    const itemLeft = (item) => item.offsetLeft;

    const closestIndex = () => {
      const currentLeft = track.scrollLeft;
      let closest = 0;
      let distance = Number.POSITIVE_INFINITY;

      items.forEach((item, index) => {
        const difference = Math.abs(itemLeft(item) - currentLeft);
        if (difference < distance) {
          distance = difference;
          closest = index;
        }
      });

      return closest;
    };

    const update = () => {
      activeIndex = closestIndex();

      if (previousButton) previousButton.disabled = activeIndex <= 0;
      if (nextButton) nextButton.disabled = activeIndex >= items.length - 1;
      if (counter) counter.textContent = `${activeIndex + 1} / ${items.length}`;
    };

    const goTo = (index) => {
      activeIndex = Math.max(0, Math.min(index, items.length - 1));
      track.scrollTo({
        left: itemLeft(items[activeIndex]),
        behavior: prefersReducedMotion ? 'auto' : 'smooth',
      });
      window.setTimeout(update, prefersReducedMotion ? 0 : 260);
    };

    previousButton?.addEventListener('click', () => goTo(activeIndex - 1));
    nextButton?.addEventListener('click', () => goTo(activeIndex + 1));

    track.addEventListener(
      'scroll',
      () => {
        if (frame) window.cancelAnimationFrame(frame);
        frame = window.requestAnimationFrame(update);
      },
      { passive: true }
    );

    if ('ResizeObserver' in window) {
      const resizeObserver = new ResizeObserver(update);
      resizeObserver.observe(track);
    }

    update();

    return {
      next: () => goTo(activeIndex >= items.length - 1 ? 0 : activeIndex + 1),
      previous: () => goTo(activeIndex - 1),
      update,
    };
  };

  /* ------------------------------------------------------------------ */
  /* Suggestions Shopify avec solutions de secours                      */
  /* ------------------------------------------------------------------ */

  const normalizePriceToCents = (product) => {
    const value = product.price ?? product.variants?.[0]?.price ?? 0;

    if (typeof value === 'number') return value;

    const normalized = Number.parseFloat(String(value).replace(',', '.'));
    if (!Number.isFinite(normalized)) return 0;

    return Math.round(normalized * 100);
  };

  const formatMoney = (cents, currency) => {
    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'fr-FR', {
        style: 'currency',
        currency: currency || 'EUR',
      }).format(Number(cents || 0) / 100);
    } catch (_) {
      return `${(Number(cents || 0) / 100).toFixed(2)} ${currency || 'EUR'}`;
    }
  };

  const productUrl = (product) =>
    product.url || `${root}products/${encodeURIComponent(product.handle || '')}`;

  const productImage = (product) => {
    if (typeof product.featured_image === 'string') return product.featured_image;
    if (product.featured_image?.src) return product.featured_image.src;
    if (product.images?.[0]?.src) return product.images[0].src;
    if (typeof product.images?.[0] === 'string') return product.images[0];
    return '';
  };

  const availableVariants = (product) =>
    (product.variants || []).filter((variant) => variant.available !== false);

  const addRecommendedVariant = async (button, variantId) => {
    const originalText = button.textContent;
    button.disabled = true;
    button.textContent = 'Ajout…';

    try {
      const response = await fetch(`${root}cart/add.js`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify({
          items: [{ id: Number(variantId), quantity: 1 }],
          sections: cartSections(),
          sections_url: window.location.pathname,
        }),
      });

      const data = await parseJsonResponse(response);
      renderCartResponse(data);
    } catch (error) {
      console.error('Erreur lors de l’ajout de la suggestion :', error);
      button.disabled = false;
      button.textContent = 'Erreur';

      window.setTimeout(() => {
        button.textContent = originalText;
      }, 1800);
    }
  };

  const buildSmartUpsellCard = (product, container) => {
    const hideExistingProducts =
      container.dataset.hideExistingProducts === 'true';
    const existingIds = new Set(
      (container.dataset.existingProductIds || '')
        .split(',')
        .map((id) => id.trim())
        .filter(Boolean)
    );

    if (hideExistingProducts && existingIds.has(String(product.id))) return null;

    const variants = availableVariants(product);
    if (!variants.length) return null;

    const card = document.createElement('article');
    card.className = 'up-cart-smart-upsell-card';
    card.setAttribute('role', 'listitem');

    const cardScheme = container.dataset.cardColorScheme;
    if (cardScheme) {
      card.classList.add(`color-${cardScheme}`, 'gradient');
    }

    const imageLink = document.createElement('a');
    imageLink.className = 'up-cart-smart-upsell-card__image';
    imageLink.href = productUrl(product);

    const source = productImage(product);
    if (source) {
      const image = document.createElement('img');
      image.src = source;
      image.alt = product.title || '';
      image.loading = 'lazy';
      image.decoding = 'async';
      imageLink.appendChild(image);
    }

    const content = document.createElement('div');
    content.className = 'up-cart-smart-upsell-card__content';

    const title = document.createElement('a');
    title.className = 'up-cart-smart-upsell-card__title';
    title.href = productUrl(product);
    title.textContent = product.title || '';

    const price = document.createElement('span');
    price.className = 'up-cart-smart-upsell-card__price';
    price.textContent = formatMoney(
      normalizePriceToCents(product),
      container.dataset.currency
    );

    content.append(title, price);

    let action;
    if ((product.variants || []).length === 1) {
      action = document.createElement('button');
      action.type = 'button';
      action.className = 'button up-cart-smart-upsell-card__button';
      action.textContent = container.dataset.addText || 'Ajouter';
      action.addEventListener('click', () =>
        addRecommendedVariant(action, variants[0].id)
      );
    } else {
      action = document.createElement('a');
      action.className =
        'button button--secondary up-cart-smart-upsell-card__button';
      action.href = productUrl(product);
      action.textContent = container.dataset.chooseText || 'Choisir';
    }

    card.append(imageLink, content, action);
    return card;
  };

  const fetchRecommendationProducts = async (productId, limit, intent) => {
    const endpoint =
      `${root}recommendations/products.json?product_id=${encodeURIComponent(productId)}` +
      `&limit=${limit}&intent=${intent}`;

    const response = await fetch(endpoint, {
      headers: { Accept: 'application/json' },
    });

    const data = await parseJsonResponse(response);
    return Array.isArray(data.products) ? data.products : [];
  };

  const fetchCollectionProducts = async (handle, limit) => {
    const safeHandle = handle || 'all';
    const endpoint =
      `${root}collections/${encodeURIComponent(safeHandle)}/products.json?limit=${Math.max(
        limit * 3,
        8
      )}`;

    const response = await fetch(endpoint, {
      headers: { Accept: 'application/json' },
    });

    const data = await parseJsonResponse(response);
    return Array.isArray(data.products) ? data.products : [];
  };

  const loadSmartProducts = async (container) => {
    const productId = container.dataset.sourceProductId;
    const source = container.dataset.source;
    const collectionHandle =
      container.dataset.collectionHandle ||
      container.dataset.fallbackCollection ||
      'all';
    const preferredIntent =
      container.dataset.intent === 'related' ? 'related' : 'complementary';
    const fallbackIntent =
      preferredIntent === 'complementary' ? 'related' : 'complementary';
    const limit = Math.min(
      10,
      Math.max(1, Number.parseInt(container.dataset.limit || '2', 10))
    );

    if (source === 'collection' && collectionHandle) {
      const collectionProducts = await fetchCollectionProducts(
        collectionHandle,
        limit
      );

      if (collectionProducts.length) return collectionProducts;

      if (container.dataset.recommendationsEnabled !== 'true' || !productId) {
        return [];
      }
    }

    let products = [];

    if (productId) {
      try {
        products = await fetchRecommendationProducts(
          productId,
          limit,
          preferredIntent
        );
      } catch (error) {
        console.info(
          'Première source de recommandations indisponible :',
          error
        );
      }

      if (!products.length) {
        try {
          products = await fetchRecommendationProducts(
            productId,
            limit,
            fallbackIntent
          );
        } catch (error) {
          console.info(
            'Seconde source de recommandations indisponible :',
            error
          );
        }
      }
    }

    if (!products.length) {
      products = await fetchCollectionProducts(
        container.dataset.fallbackCollection || 'all',
        limit
      );
    }

    return products;
  };

  const initializeSmartUpsellCarousel = (container, list) => {
    if (container.dataset.layout !== 'navigation') return;

    setupCartCarousel({
      container,
      track: list,
      previousButton: container.querySelector('[data-smart-upsell-prev]'),
      nextButton: container.querySelector('[data-smart-upsell-next]'),
      counter: container.querySelector('[data-smart-upsell-counter]'),
      itemSelector: '.up-cart-smart-upsell-card',
      controls: container.querySelector('[data-smart-upsell-controls]'),
    });
  };

  const initializeSmartUpsellButtons = (container) => {
    container.querySelectorAll('[data-smart-upsell-add]').forEach((button) => {
      if (button.dataset.initialized === 'true') return;

      button.dataset.initialized = 'true';
      button.addEventListener('click', () =>
        addRecommendedVariant(button, button.dataset.smartUpsellAdd)
      );
    });
  };

  const initSmartUpsells = async (container) => {
    if (container.dataset.initialized === 'true') return;
    container.dataset.initialized = 'true';

    const list = container.querySelector('[data-smart-upsell-list]');
    if (!list) return;

    initializeSmartUpsellButtons(container);

    const serverRenderedCards = list.querySelectorAll(
      '.up-cart-smart-upsell-card'
    );

    if (serverRenderedCards.length) {
      container.hidden = false;
      initializeSmartUpsellCarousel(container, list);
      return;
    }

    if (
      container.dataset.source !== 'collection' &&
      !container.dataset.sourceProductId
    ) {
      return;
    }

    try {
      const products = await loadSmartProducts(container);
      const limit = Math.max(
        1,
        Number.parseInt(container.dataset.limit || '2', 10)
      );
      const cards = products
        .map((product) => buildSmartUpsellCard(product, container))
        .filter(Boolean)
        .slice(0, limit);

      if (!cards.length) {
        const errorTarget = container.querySelector('[data-smart-upsell-error]');
        if (errorTarget) {
          errorTarget.hidden = false;
          errorTarget.textContent =
            container.dataset.source === 'collection'
              ? 'Aucun produit disponible dans cette collection.'
              : 'Aucune suggestion disponible pour le moment.';
        }
        container.hidden = false;
        return;
      }

      list.replaceChildren(...cards);
      container.hidden = false;
      initializeSmartUpsellCarousel(container, list);
    } catch (error) {
      console.error('Erreur de recommandations Shopify :', error);
      const errorTarget = container.querySelector('[data-smart-upsell-error]');
      if (errorTarget) errorTarget.hidden = false;
      container.hidden = false;
    }
  };

  /* ------------------------------------------------------------------ */
  /* Avis clients du panier                                             */
  /* ------------------------------------------------------------------ */

  const initReviewExpanders = (container) => {
    container.querySelectorAll('[data-cart-review-card]').forEach((card) => {
      const text = card.querySelector('[data-cart-review-text]');
      const button = card.querySelector('[data-cart-review-toggle]');
      if (!text || !button || button.dataset.initialized === 'true') return;

      button.dataset.initialized = 'true';

      window.requestAnimationFrame(() => {
        const isOverflowing = text.scrollHeight > text.clientHeight + 1;
        button.hidden = !isOverflowing;
      });

      button.addEventListener('click', () => {
        const expanded = card.classList.toggle('is-expanded');
        button.textContent = expanded
          ? button.dataset.lessText || 'Réduire'
          : button.dataset.moreText || 'Lire plus';
      });
    });
  };

  const initCartReviews = (container) => {
    if (container.dataset.initialized === 'true') return;
    container.dataset.initialized = 'true';

    initReviewExpanders(container);

    if (container.dataset.layout !== 'navigation') return;

    const track = container.querySelector('[data-cart-reviews-track]');
    const carousel = setupCartCarousel({
      container,
      track,
      previousButton: container.querySelector('[data-cart-reviews-prev]'),
      nextButton: container.querySelector('[data-cart-reviews-next]'),
      counter: container.querySelector('[data-cart-reviews-counter]'),
      itemSelector: '.up-cart-review-card',
      controls: container.querySelector('[data-cart-reviews-controls]'),
    });

    if (!carousel || container.dataset.autoplay !== 'true') return;

    const interval = Math.max(
      3000,
      Number.parseInt(container.dataset.autoplaySpeed || '5000', 10)
    );
    let autoplayTimer = null;

    const start = () => {
      if (autoplayTimer || document.hidden) return;
      autoplayTimer = window.setInterval(() => carousel.next(), interval);
    };

    const stop = () => {
      if (!autoplayTimer) return;
      window.clearInterval(autoplayTimer);
      autoplayTimer = null;
    };

    container.addEventListener('pointerenter', stop);
    container.addEventListener('pointerleave', start);
    container.addEventListener('focusin', stop);
    container.addEventListener('focusout', start);
    document.addEventListener('visibilitychange', () => {
      if (document.hidden) stop();
      else start();
    });

    start();
  };

  /* ------------------------------------------------------------------ */
  /* Cadeaux automatiques                                               */
  /* ------------------------------------------------------------------ */

  const syncAutoGifts = async (config) => {
    if (giftSyncInProgress || !config) return;

    const goals = [...config.querySelectorAll('[data-auto-gift-goal]')]
      .map((goal) => ({
        key: goal.dataset.key,
        threshold: Number.parseInt(goal.dataset.threshold || '0', 10),
        variantId: Number.parseInt(goal.dataset.variantId || '0', 10),
        label: goal.dataset.label || 'Cadeau automatique',
      }))
      .filter((goal) => goal.threshold > 0 && goal.variantId > 0);

    if (!goals.length) return;

    giftSyncInProgress = true;

    try {
      const cartResponse = await fetch(`${root}cart.js`, {
        headers: { Accept: 'application/json' },
      });
      const cart = await parseJsonResponse(cartResponse);

      const qualifyingTotal = (cart.items || []).reduce((total, item) => {
        const isGift = item.properties?._up_auto_gift;
        return isGift ? total : total + Number(item.final_line_price || 0);
      }, 0);

      const existingByKey = new Map();
      (cart.items || []).forEach((item) => {
        const key = item.properties?._up_auto_gift;
        if (key) existingByKey.set(key, item);
      });

      const updates = {};
      const additions = [];

      goals.forEach((goal) => {
        const reached = qualifyingTotal >= goal.threshold;
        const existing = existingByKey.get(goal.key);

        if (reached && !existing) {
          additions.push({
            id: goal.variantId,
            quantity: 1,
            properties: {
              _up_auto_gift: goal.key,
              _up_auto_gift_label: goal.label,
            },
          });
        } else if (reached && existing && existing.quantity !== 1) {
          updates[existing.key] = 1;
        } else if (
          !reached &&
          existing &&
          config.dataset.removeBelow === 'true'
        ) {
          updates[existing.key] = 0;
        }
      });

      let latestResponse = null;

      if (Object.keys(updates).length) {
        const updateResponse = await fetch(`${root}cart/update.js`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
          },
          body: JSON.stringify({
            updates,
            sections: cartSections(),
            sections_url: window.location.pathname,
          }),
        });
        latestResponse = await parseJsonResponse(updateResponse);
      }

      if (additions.length) {
        const addResponse = await fetch(`${root}cart/add.js`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
          },
          body: JSON.stringify({
            items: additions,
            sections: cartSections(),
            sections_url: window.location.pathname,
          }),
        });
        latestResponse = await parseJsonResponse(addResponse);
      }

      if (latestResponse) renderCartResponse(latestResponse);
    } catch (error) {
      console.error(
        'Erreur de synchronisation des cadeaux automatiques :',
        error
      );
    } finally {
      window.setTimeout(() => {
        giftSyncInProgress = false;
      }, 450);
    }
  };

  /* ------------------------------------------------------------------ */
  /* Initialisation et réinitialisation AJAX                            */
  /* ------------------------------------------------------------------ */

  const initializeEnhancements = () => {
    updateCountdowns();

    document
      .querySelectorAll('[data-smart-cart-upsells]')
      .forEach(initSmartUpsells);

    document
      .querySelectorAll('[data-cart-customer-reviews]')
      .forEach(initCartReviews);

    const giftConfig = document.querySelector('[data-cart-auto-gifts]');
    if (giftConfig) syncAutoGifts(giftConfig);
  };

  const scheduleInitialization = () => {
    window.clearTimeout(enhancementTimer);
    enhancementTimer = window.setTimeout(initializeEnhancements, 120);
  };

  const observer = new MutationObserver((mutations) => {
    const relevantChange = mutations.some((mutation) =>
      [...mutation.addedNodes].some(
        (node) =>
          node.nodeType === Node.ELEMENT_NODE &&
          (node.matches?.(
            '[data-cart-countdown], [data-smart-cart-upsells], [data-cart-customer-reviews], [data-cart-auto-gifts], #CartDrawer'
          ) ||
            node.querySelector?.(
              '[data-cart-countdown], [data-smart-cart-upsells], [data-cart-customer-reviews], [data-cart-auto-gifts]'
            ))
      )
    );

    if (relevantChange) scheduleInitialization();
  });

  observer.observe(document.body, { childList: true, subtree: true });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initializeEnhancements, {
      once: true,
    });
  } else {
    initializeEnhancements();
  }
})();
